/**
 * Email Context Builder - Frontend Version
 * 
 * IMPORTANT: This context builder must stay in sync with:
 * - Functions: functions/src/services/emailContextBuilder.ts
 * 
 * When modifying this file, consider if the same change is needed in the Functions version.
 * Both builders should produce identical output for the same input.
 */

import { Registration } from '../types';
import { getEventEdition } from './eventEditionService';
import { formatShortDate, formatDateTime } from '../utils/dateFormatter';

 const DEFAULT_STATIONARY_IMAGE_URL = 'https://krultra.no/assets/email/krultra_stationary_top.png';
 const DEFAULT_APP_BASE_URL = 'https://krultra.no';

const resolveAppBaseUrl = (): string => {
  const configuredUrl =
    process.env.REACT_APP_RUNNERSHUB_URL ||
    process.env.REACT_APP_BASE_URL ||
    process.env.APP_BASE_URL ||
    process.env.REACT_APP_PUBLIC_URL ||
    process.env.PUBLIC_URL;

  if (configuredUrl) {
    return String(configuredUrl).replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }

  return DEFAULT_APP_BASE_URL;
};

/**
 * Email context interface - must match Functions version
 * 
 * SYNC: Keep in sync with functions/src/services/emailContextBuilder.ts
 */
export interface EmailContext {
  // Core registration fields
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  raceDistance: string;
  representing: string;
  nationality: string;
  dateOfBirth: string;
  phoneNumber: string;
  phoneCountryCode: string;
  comments: string;
  languagePreference: string;
  languagePreferenceVerbose: string;
  preferredLanguage: string;
  status: string;
  registrationNumber: string;
  personId: string;
  
  // Event info
  editionId: string;
  eventName: string;
  eventShortName: string;
  eventEdition: string;
  appBaseUrl: string;
  
  // License fields
  hasYearLicense: boolean;
  licenseNumber: string;
  
  depositAmount?: number;
  originalStripeFee?: number | string;
  depositAdjustment?: number;
  refundAmount?: number;
  refundMethod?: string;
  adminComment?: string;

  // Payment fields (with aliases for template compatibility)
  paymentAmount: number;
  paymentPaid: number;
  paymentRequired: number;
  paymentMade: number;
  paymentOutstanding: number;
  paymentStatus: string;
  
  // Stationary image URLs
  krultraStationaryImageUrl: string;
  headerImageUrl: string;
  logoUrl: string;
  
  // Dates
  today: string;
  updatedAt: string;
  waitinglistExpires: string;
  dateYearCurrent: string;
  dateYearPrevious: string;
  dateYearNext: string;
  dateToday: string;
  dateEventEditionStartTime: string;
  datetimeEventEditionRegistrationOpens: string;
  datetimeEventEditionRegistrationDeadline: string;
}

/**
 * Status translations - must match Functions version
 * 
 * SYNC: Keep in sync with functions/src/services/emailContextBuilder.ts
 */
const statusTranslations: Record<string, Record<string, string>> = {
  'en': {
    pending: 'Pending',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
    expired: 'Expired',
    waitlist: 'Waitlist',
    'payment-pending': 'Payment Pending'
  },
  'no': {
    pending: 'Ubekreftet',
    confirmed: 'Bekreftet',
    cancelled: 'Kansellert',
    expired: 'Utløpt',
    waitlist: 'Venteliste',
    'payment-pending': 'Betaling ventende'
  }
};

/**
 * Language display names - must match Functions version
 * 
 * SYNC: Keep in sync with functions/src/services/emailContextBuilder.ts
 */
const languageNames: Record<string, string> = {
  'en': 'English',
  'no': 'Norsk',
  'nb': 'Norsk',
  'nn': 'Norsk',
  'nb-NO': 'Norsk'
};

/**
 * Gets the localized display name for a registration status
 * 
 * SYNC: Keep in sync with functions/src/services/emailContextBuilder.ts
 */
export function getStatusDisplayName(status: string, locale: string): string {
  const lang = locale === 'no' || locale === 'nb-NO' || locale === 'nb' ? 'no' : 'en';
  return statusTranslations[lang]?.[status] || status;
}

/**
 * Gets the verbose display name for a language preference
 * 
 * SYNC: Keep in sync with functions/src/services/emailContextBuilder.ts
 */
export function getLanguagePreferenceVerbose(locale: string): string {
  return languageNames[locale] || locale;
}

/**
 * Formats a date to a locale-aware long date string
 * Norwegian: "8. april 2026", English: "8 April 2026"
 * 
 * SYNC: Keep in sync with functions/src/services/emailContextBuilder.ts
 */
function formatLocalizedDate(date: Date, locale: string): string {
  const isNorwegian = locale === 'no' || locale === 'nb' || locale === 'nb-NO' || locale === 'nn';
  return date.toLocaleDateString(isNorwegian ? 'nb-NO' : 'en-GB', {
    day: 'numeric', month: 'long', year: 'numeric'
  });
}

/**
 * Formats a date to a locale-aware date+time string (date + HH:MM)
 * Norwegian: "8. april 2026 kl. 10:00", English: "8 April 2026 at 10:00"
 * 
 * SYNC: Keep in sync with functions/src/services/emailContextBuilder.ts
 */
function formatLocalizedDatetime(date: Date, locale: string): string {
  const isNorwegian = locale === 'no' || locale === 'nb' || locale === 'nb-NO' || locale === 'nn';
  return date.toLocaleString(isNorwegian ? 'nb-NO' : 'en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

/**
 * Formats a date value from various formats to DD.MM.YYYY string
 * 
 * SYNC: Keep in sync with functions/src/services/emailContextBuilder.ts
 */
function formatDateValue(dateValue: any): string {
  if (!dateValue) return '';
  
  let date: Date;
  
  // Handle Firestore Timestamp (has toDate method)
  if (dateValue && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  }
  // Handle milliseconds as number
  else if (typeof dateValue === 'number' && dateValue > 1000000000) {
    date = new Date(dateValue);
  }
  // Handle date string
  else if (typeof dateValue === 'string') {
    date = new Date(dateValue);
  }
  // Handle Date object
  else if (dateValue instanceof Date) {
    date = dateValue;
  }
  // Fallback
  else {
    return '';
  }
  
  // Check for invalid date
  if (isNaN(date.getTime())) {
    return '';
  }
  
  // Format as DD.MM.YYYY
  return date.toLocaleDateString('nb-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

/**
 * Builds a complete, localized email context for any email type
 * 
 * IMPORTANT: This function must stay in sync with:
 * - Functions: functions/src/services/emailContextBuilder.ts
 * 
 * When modifying this function, consider if the same change is needed in the Functions version.
 * 
 * @param registration - Registration data (can be partial)
 * @returns Complete email context with all variables localized and formatted
 */
export async function buildEmailContext(
  registration: Partial<Registration> & { [key: string]: any }
): Promise<EmailContext> {
  const locale: string = registration.languagePreference || 'en';
  
  // 1. Get localized race distance name
  // SYNC: Same logic exists in functions/src/services/emailContextBuilder.ts
  let raceDistanceDisplayName = registration.raceDistance || '';
  try {
    if (registration.raceDistance && registration.editionId) {
      const eventEdition = await getEventEdition(registration.editionId);
      if (eventEdition?.raceDistances) {
        const distance = eventEdition.raceDistances.find(
          (rd: any) => rd.id === registration.raceDistance
        );
        if (distance) {
          raceDistanceDisplayName = (locale === 'no' || locale === 'nb-NO') && distance.displayName_no
            ? distance.displayName_no
            : distance.displayName || distance.id;
        }
      }
    }
  } catch (error) {
    // TODO: [LOGGING-SYSTEM] Log this error when logging system is implemented
    console.warn('[buildEmailContext] Failed to get race distance display name:', error);
  }

  // 2. Get event info
  // SYNC: Same logic exists in functions/src/services/emailContextBuilder.ts
  let eventName = '';
  let eventShortName = '';
  let eventEdition = '';
  let dateEventEditionStartTime = '';
  let datetimeEventEditionRegistrationOpens = '';
  let datetimeEventEditionRegistrationDeadline = '';
  try {
    if (registration.editionId) {
      const edition = await getEventEdition(registration.editionId);
      if (edition) {
        eventName = edition.eventName || '';
        eventShortName = edition.eventShortName || '';
        eventEdition = edition.edition?.toString() || '';
        const toDate = (v: any): Date | null => {
          if (!v) return null;
          return typeof v.toDate === 'function' ? v.toDate() : new Date(v);
        };
        const startDate = toDate(edition.startTime);
        if (startDate) dateEventEditionStartTime = formatLocalizedDate(startDate, locale);
        const regOpens = toDate(edition.registrationOpens);
        if (regOpens) datetimeEventEditionRegistrationOpens = formatLocalizedDatetime(regOpens, locale);
        const regDeadline = toDate(edition.registrationDeadline);
        if (regDeadline) datetimeEventEditionRegistrationDeadline = formatLocalizedDatetime(regDeadline, locale);
      }
    }
  } catch (error) {
    // TODO: [LOGGING-SYSTEM] Log this error when logging system is implemented
    console.warn('[buildEmailContext] Failed to get event info:', error);
  }

  // 3. Build complete context
  // SYNC: Same structure exists in functions/src/services/emailContextBuilder.ts
  const context: EmailContext = {
    ...((registration as any).depositAmount !== undefined ? {
      depositAmount: (registration as any).depositAmount, originalStripeFee: (registration as any).originalStripeFee,
      depositAdjustment: (registration as any).depositAdjustment, refundAmount: (registration as any).refundAmount,
      refundMethod: (registration as any).refundMethod, adminComment: (registration as any).adminComment,
    } : {}),
    // Core registration fields
    id: registration.id || '',
    email: registration.email || '',
    firstName: registration.firstName || '',
    lastName: registration.lastName || '',
    name: `${registration.firstName || ''} ${registration.lastName || ''}`.trim(),
    raceDistance: raceDistanceDisplayName,
    representing: registration.representing || '',
    nationality: registration.nationality || '',
    dateOfBirth: formatDateValue(registration.dateOfBirth),
    phoneNumber: registration.phoneNumber || '',
    phoneCountryCode: registration.phoneCountryCode || '',
    comments: registration.comments || '',
    languagePreference: locale,
    languagePreferenceVerbose: getLanguagePreferenceVerbose(locale),
    preferredLanguage: getLanguagePreferenceVerbose(locale),
    status: getStatusDisplayName(registration.status || 'pending', locale),
    registrationNumber: registration.registrationNumber?.toString() || '',
    personId: registration.personId?.toString() || '',
    
    // Event info
    editionId: registration.editionId || '',
    eventName,
    eventShortName,
    eventEdition,
    appBaseUrl: resolveAppBaseUrl(),
    
    // License fields
    hasYearLicense: registration.hasYearLicense || false,
    licenseNumber: registration.licenseNumber || '',
    
    // Payment fields (with aliases for template compatibility)
    // SYNC: Same aliases exist in functions/src/services/emailContextBuilder.ts
    paymentAmount: registration.paymentRequired || 0,
    paymentPaid: registration.paymentMade || 0,
    paymentRequired: registration.paymentRequired || 0,
    paymentMade: registration.paymentMade || 0,
    paymentOutstanding: Math.max(0, (registration.paymentRequired || 0) - (registration.paymentMade || 0)),
    paymentStatus: registration.paymentStatus || 'pending',
    
    // Stationary image URLs
    // SYNC: Same URLs exist in functions/src/services/emailContextBuilder.ts
    krultraStationaryImageUrl:
      process.env.REACT_APP_KRULTRA_STATIONARY_IMAGE_URL ||
      process.env.KRULTRA_STATIONARY_IMAGE_URL ||
      DEFAULT_STATIONARY_IMAGE_URL,
    headerImageUrl:
      process.env.REACT_APP_KRULTRA_HEADER_IMAGE_URL ||
      process.env.KRULTRA_HEADER_IMAGE_URL ||
      process.env.REACT_APP_KRULTRA_STATIONARY_IMAGE_URL ||
      process.env.KRULTRA_STATIONARY_IMAGE_URL ||
      DEFAULT_STATIONARY_IMAGE_URL,
    logoUrl:
      process.env.REACT_APP_KRULTRA_LOGO_URL ||
      process.env.KRULTRA_LOGO_URL ||
      process.env.REACT_APP_KRULTRA_STATIONARY_IMAGE_URL ||
      process.env.KRULTRA_STATIONARY_IMAGE_URL ||
      DEFAULT_STATIONARY_IMAGE_URL,
    
    // Dates
    today: formatShortDate(new Date()),
    updatedAt: registration.updatedAt ? formatDateTime(registration.updatedAt) : '',
    waitinglistExpires: registration.waitinglistExpires ? formatShortDate(registration.waitinglistExpires) : '',
    dateYearCurrent: new Date().getFullYear().toString(),
    dateYearPrevious: (new Date().getFullYear() - 1).toString(),
    dateYearNext: (new Date().getFullYear() + 1).toString(),
    dateToday: formatLocalizedDate(new Date(), locale),
    dateEventEditionStartTime,
    datetimeEventEditionRegistrationOpens,
    datetimeEventEditionRegistrationDeadline,
  };

  return context;
}

/**
 * Compares two context objects and returns differences
 * Used to detect mismatches between provided context and builder output
 * 
 * @param builderContext - Context generated by buildEmailContext
 * @param providedContext - Context passed by caller (optional)
 * @returns Object with mismatched fields and their values
 */
export function compareContexts(
  builderContext: Partial<EmailContext>,
  providedContext: Record<string, any>
): Record<string, { builder: any; provided: any }> {
  const mismatches: Record<string, { builder: any; provided: any }> = {};
  
  // Fields to compare (skip complex objects and fields that are expected to differ)
  const fieldsToCompare = [
    'raceDistance',
    'status',
    'languagePreferenceVerbose',
    'preferredLanguage',
    'paymentAmount',
    'paymentPaid',
    'paymentOutstanding',
    'dateOfBirth',
    'name'
  ];
  
  for (const field of fieldsToCompare) {
    const builderValue = (builderContext as any)[field];
    const providedValue = providedContext[field];
    
    // Only compare if both have values (skip if provided is undefined)
    if (providedValue !== undefined && builderValue !== providedValue) {
      // Special case: numeric comparison (0 vs undefined)
      if (typeof builderValue === 'number' && (providedValue === undefined || providedValue === null)) {
        continue;
      }
      
      mismatches[field] = {
        builder: builderValue,
        provided: providedValue
      };
    }
  }
  
  return mismatches;
}

/**
 * Logs context comparison results for debugging
 * 
 * @param mismatches - Result from compareContexts
 * @param emailType - Type of email being sent
 */
export function logContextMismatches(
  mismatches: Record<string, { builder: any; provided: any }>,
  emailType: string
): void {
  const mismatchCount = Object.keys(mismatches).length;
  
  if (mismatchCount > 0 && process.env.NODE_ENV === 'development') {
    // TODO: [LOGGING-SYSTEM] Write to logs collection when logging system is implemented
    console.debug(
      `[buildEmailContext] Context mismatches detected for ${emailType}; builder values will be used:`,
      mismatches
    );
  }
}
